import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CombatLogManager, { COMBAT_LOG_TYPES } from '../../../utils/CombatLogManager';
import './LatestResolutionCard.css';

const RESOLUTION_TYPES = new Set([
  COMBAT_LOG_TYPES.ACTION_STANDARD,
  COMBAT_LOG_TYPES.ACTION_ATTACK,
  COMBAT_LOG_TYPES.ACTION_SPELL,
  COMBAT_LOG_TYPES.ACTION_BONUS,
  COMBAT_LOG_TYPES.DAMAGE_DEALT,
  COMBAT_LOG_TYPES.HEALING_APPLIED,
  COMBAT_LOG_TYPES.ACTION_REACTION,
]);

const finiteNumber = (...values) => values.find((value) => (
  value !== null
  && value !== undefined
  && value !== ''
  && Number.isFinite(Number(value))
));

const displayNumber = (value) => Number.isFinite(Number(value)) ? String(Number(value)) : null;

const formatSigned = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) return '';
  return numeric > 0 ? ` + ${numeric}` : ` - ${Math.abs(numeric)}`;
};

const formatAttackRoll = (roll, targetAc) => {
  if (!roll || typeof roll !== 'object') return null;
  const rolls = Array.isArray(roll.rolls) ? roll.rolls.filter((value) => Number.isFinite(Number(value))) : [];
  const selected = finiteNumber(roll.selected, roll.chosenValue, roll.d20Roll, roll.roll);
  const modifier = finiteNumber(roll.modifier, roll.attackBonus);
  const total = finiteNumber(roll.total);
  const rollLabel = rolls.length > 1
    ? `d20 [${rolls.join(', ')}] → ${displayNumber(selected)}`
    : `d20 ${displayNumber(selected) || '?'}`;
  const target = Number.isFinite(Number(targetAc)) ? ` vs AC ${Number(targetAc)}` : '';
  return `${rollLabel}${formatSigned(modifier)} = ${displayNumber(total) || '?'}${target}`;
};

const formatDamageDetails = (damageDetails) => {
  if (!Array.isArray(damageDetails)) return [];
  return damageDetails.map((detail) => {
    const formula = detail?.dice || detail?.formula;
    const rolls = Array.isArray(detail?.rolls) ? ` [${detail.rolls.join(', ')}]` : '';
    const bonus = formatSigned(detail?.bonus);
    const finalTotal = finiteNumber(detail?.finalAmount, detail?.total, detail?.amount);
    const originalTotal = finiteNumber(detail?.originalTotal, detail?.rawTotal, finalTotal);
    const type = detail?.type || detail?.damageType;
    const calculation = formula
      ? `${formula}${rolls}${bonus}${originalTotal !== undefined ? ` = ${Number(originalTotal)}` : ''}`
      : (originalTotal !== undefined ? String(Number(originalTotal)) : null);
    const recordedTransformations = Array.isArray(detail?.transformations)
      ? detail.transformations.map((entry) => `${entry.source}: ${entry.before} → ${entry.after}`)
      : [];
    const mitigation = String(detail?.mitigation || '').trim().toLowerCase();
    const mitigationLabel = {
      resistant: 'Resistance',
      vulnerable: 'Vulnerability',
      immune: 'Immunity'
    }[mitigation] || '';
    const transformations = [
      ...recordedTransformations,
      ...(mitigationLabel && originalTotal !== undefined && finalTotal !== undefined && Number(originalTotal) !== Number(finalTotal)
        ? [`${mitigationLabel}: ${Number(originalTotal)} → ${Number(finalTotal)}`]
        : [])
    ].join(' · ');
    return [[calculation, type].filter(Boolean).join(' '), transformations].filter(Boolean).join(' — ');
  }).filter(Boolean);
};

const formatEffectDetails = (effects) => {
  if (!Array.isArray(effects)) return [];
  return effects.flatMap((effect) => {
    const target = effect?.target?.name || effect?.target || null;
    const save = effect?.savingThrow;
    const details = [];
    if (save) {
      const ability = String(save.ability || 'save').toUpperCase();
      const roll = finiteNumber(save.roll, save.d20Roll);
      const modifier = finiteNumber(save.modifier);
      const total = finiteNumber(save.total);
      const dc = finiteNumber(save.dc);
      const math = roll !== undefined
        ? `d20 ${Number(roll)}${formatSigned(modifier)} = ${displayNumber(total) || '?'}${dc !== undefined ? ` vs DC ${Number(dc)}` : ''}`
        : (dc !== undefined ? `DC ${Number(dc)}` : 'Saving throw');
      details.push(`${target ? `${target}: ` : ''}${ability} ${math} — ${save.success ? 'saved' : 'failed'}`);
    }

    const damage = effect?.damage;
    if (damage) {
      const recordedDamage = (save?.success === true
        && Number.isFinite(Number(damage?.originalTotal))
        && Number.isFinite(Number(damage?.total))
        && Number(damage.originalTotal) !== Number(damage.total))
        ? {
            ...damage,
            transformations: [
              ...(Array.isArray(damage.transformations) ? damage.transformations : []),
              {
                source: 'Successful save',
                before: Number(damage.originalTotal),
                after: Number(damage.total)
              }
            ]
          }
        : damage;
      const damageMath = formatDamageDetails([recordedDamage]);
      details.push(...damageMath.map((line) => `${target ? `${target}: ` : ''}${line}`));
    }
    return details;
  });
};

const resolveHpChange = (data, result) => {
  const beforeSnapshot = data?.snapshots?.before?.[0]?.hpBefore;
  const afterSnapshot = data?.snapshots?.after?.[0]?.hpAfter;
  const before = finiteNumber(result?.targetHpBefore, beforeSnapshot?.current);
  const after = finiteNumber(result?.targetHpRemaining, afterSnapshot?.current);
  return before !== undefined && after !== undefined
    ? { before: Number(before), after: Number(after) }
    : null;
};

/**
 * Convert the durable log entry into immediate, human-readable action feedback.
 * The card never recalculates mechanics; it only explains the recorded result.
 * date_of_change: 2026-08-11
 */
export const buildResolutionCardModel = (entry, getCombatantName = () => '') => {
  if (!entry || !RESOLUTION_TYPES.has(entry.type)) return null;
  const data = entry.data || {};
  const result = data.result || {};
  const actionName = data.action?.name
    || (entry.type === COMBAT_LOG_TYPES.DAMAGE_DEALT ? 'Damage applied' : null)
    || (entry.type === COMBAT_LOG_TYPES.HEALING_APPLIED ? 'Healing applied' : null)
    || 'Action resolved';
  const actorName = data.actor?.name || getCombatantName(entry.combatantId) || 'Combatant';
  const explicitTargets = Array.isArray(data.targets)
    ? data.targets.map((target) => target?.name).filter(Boolean)
    : [];

  const effects = Array.isArray(result.effects) ? result.effects : [];
  const effectTargets = effects.map((effect) => effect?.target?.name || effect?.target).filter(Boolean);
  const targets = [...new Set([...explicitTargets, ...effectTargets])];
  const attackRoll = result.attackRoll
    || ((result.selected !== undefined || Array.isArray(result.rolls)) ? result : result.roll);
  const hit = result.hit;
  const saveEffects = effects.filter((effect) => effect?.type === 'savingThrow');
  const failedSaves = saveEffects.filter((effect) => effect?.savingThrow?.success === false).length;
  const successfulSaves = saveEffects.filter((effect) => effect?.savingThrow?.success === true).length;
  const hasSingleSave = result.success !== undefined && result.dc !== undefined;
  const numericDamage = finiteNumber(
    typeof result.damage === 'object' ? result.damage?.total : result.damage,
    result.totalDamage,
    data.damage,
  );
  const numericHealing = finiteNumber(result.healing, result.totalHealing, data.healing);
  const hpChange = resolveHpChange(data, result);
  const hasPositiveDamage = numericDamage !== undefined && Number(numericDamage) > 0;
  const hasPositiveHealing = numericHealing !== undefined && Number(numericHealing) > 0;

  let outcome = 'Resolved';
  let tone = 'neutral';
  if (hit === true) {
    outcome = attackRoll?.isCritical || attackRoll?.natural20 || result.natural20 ? 'Critical hit' : 'Hit';
    tone = 'success';
  } else if (hit === false) {
    outcome = 'Miss';
    tone = 'warning';
  } else if (saveEffects.length > 0) {
    const singleSaveSuccess = saveEffects[0]?.savingThrow?.success;
    outcome = saveEffects.length === 1
      ? (singleSaveSuccess === true
          ? 'Save succeeded'
          : (singleSaveSuccess === false ? 'Save failed' : 'Save resolved'))
      : `${failedSaves} failed · ${successfulSaves} saved`;
    tone = failedSaves > 0 ? 'success' : 'neutral';
  } else if (hasSingleSave) {
    outcome = result.success ? 'Save succeeded' : 'Save failed';
    tone = result.success ? 'neutral' : 'success';
  } else if (hasPositiveDamage) {
    outcome = 'Damage';
    tone = 'success';
  } else if (hasPositiveHealing) {
    outcome = 'Healing';
    tone = 'healing';
  } else if (numericDamage !== undefined && numericHealing === undefined) {
    // date_of_change: 2026-08-20
    // WHAT: Preserve an explicit zero-damage resolution without calling it healing.
    // WHY: Spell result wrappers commonly include both totals with the unused total set to zero;
    // presence alone does not identify which kind of effect actually resolved.
    outcome = 'Damage';
    tone = 'neutral';
  } else if (numericHealing !== undefined && numericDamage === undefined) {
    outcome = 'Healing';
    tone = 'neutral';
  }

  const facts = [];
  if (targets.length > 0) facts.push(targets.join(', '));
  if (numericDamage !== undefined && Number(numericDamage) > 0) facts.push(`${Number(numericDamage)} damage`);
  if (numericHealing !== undefined && Number(numericHealing) > 0) facts.push(`${Number(numericHealing)} healing`);
  if (hpChange) facts.push(`HP ${hpChange.before} → ${hpChange.after}`);

  const details = [];
  const attackMath = formatAttackRoll(attackRoll, result.targetAC ?? result.targetAc);
  if (attackMath) details.push(attackMath);
  details.push(...formatDamageDetails(result.damageDetails || result.damageRoll?.details || result.damage?.details));
  details.push(...formatEffectDetails(effects));
  if (hasSingleSave) {
    const ability = String(result.saveAbility || 'save').toUpperCase();
    const roll = finiteNumber(result.selected, result.d20Roll, result.roll);
    details.push(`${ability} d20 ${displayNumber(roll) || '?'}${formatSigned(result.modifier)} = ${displayNumber(result.total) || '?'} vs DC ${result.dc}`);
  } else if (result.dc !== undefined) {
    details.push(`Save DC ${result.dc}`);
  }

  return {
    id: entry.id || `${entry.type}:${entry.timestamp || ''}`,
    typeLabel: entry.type === COMBAT_LOG_TYPES.ACTION_SPELL
      ? 'Spell result'
      : entry.type === COMBAT_LOG_TYPES.DAMAGE_DEALT
        ? 'Damage resolution'
        : entry.type === COMBAT_LOG_TYPES.HEALING_APPLIED
          ? 'Healing resolution'
          : entry.type === COMBAT_LOG_TYPES.ACTION_REACTION
            ? 'Reaction result'
            : 'Action result',
    actorName,
    actionName,
    outcome,
    tone,
    summary: facts.join(' · ') || entry.description || 'The action was recorded.',
    details,
  };
};

const latestResolutionEntry = () => {
  const log = CombatLogManager.getCombatLog();
  return [...log].reverse().find((entry) => RESOLUTION_TYPES.has(entry?.type)) || null;
};

const LatestResolutionCard = ({ entry = undefined }) => {
  const [observedEntry, setObservedEntry] = useState(() => entry === undefined ? latestResolutionEntry() : entry);
  const [dismissedId, setDismissedId] = useState(null);

  const refresh = useCallback(() => {
    if (entry === undefined) setObservedEntry(latestResolutionEntry());
  }, [entry]);

  useEffect(() => {
    if (entry !== undefined) setObservedEntry(entry);
  }, [entry]);

  useEffect(() => {
    if (entry !== undefined) return undefined;
    window.addEventListener('combat-log-updated', refresh);
    window.addEventListener('websocket-combat-log-entry', refresh);
    return () => {
      window.removeEventListener('combat-log-updated', refresh);
      window.removeEventListener('websocket-combat-log-entry', refresh);
    };
  }, [entry, refresh]);

  const model = useMemo(() => buildResolutionCardModel(
    observedEntry,
    (combatantId) => CombatLogManager.getCombatantName(combatantId)
  ), [observedEntry]);

  if (!model || dismissedId === model.id) return null;

  return (
    <section className={`latest-resolution-card latest-resolution-card--${model.tone}`} aria-live="polite" aria-label="Latest combat result">
      <div className="latest-resolution-card__header">
        <div>
          <span className="latest-resolution-card__kicker">{model.typeLabel}</span>
          <h3>{model.actorName} · {model.actionName}</h3>
        </div>
        <button
          type="button"
          className="latest-resolution-card__dismiss"
          onClick={() => setDismissedId(model.id)}
          aria-label="Dismiss latest combat result"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div className="latest-resolution-card__outcome-row">
        <span className="latest-resolution-card__outcome">{model.outcome}</span>
        <span className="latest-resolution-card__summary">{model.summary}</span>
      </div>
      {model.details.length > 0 && (
        <details className="latest-resolution-card__details">
          <summary>Show roll details</summary>
          <ul>
            {model.details.map((detail, index) => <li key={`${model.id}:${index}`}>{detail}</li>)}
          </ul>
        </details>
      )}
    </section>
  );
};

LatestResolutionCard.propTypes = {
  entry: PropTypes.object,
};

export default LatestResolutionCard;
